/**
 * Component to display and edit recurrence settings for shift series
 * Shows recurrence type and repetitions; can be read-only or editable
 */

import { useEffect, useState } from 'react';
import { useLingui } from '@lingui/react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Repeat, Calendar, CalendarDays, CalendarRange } from 'lucide-react';

export const RecurrenceType = {
  DAILY: 'DAILY',
  WEEKDAYS: 'WEEKDAYS',
  WEEKLY: 'WEEKLY',
} as const;

export type RecurrenceTypeValue =
  (typeof RecurrenceType)[keyof typeof RecurrenceType];

export interface RecurrenceSettingsProps {
  recurrenceType: RecurrenceTypeValue;
  repetitions: number;
  onRecurrenceTypeChange?: (value: RecurrenceTypeValue) => void;
  onRepetitionsChange?: (value: number) => void;
  readOnly?: boolean;
}

export function RecurrenceSettings({
  recurrenceType,
  repetitions,
  onRecurrenceTypeChange,
  onRepetitionsChange,
  readOnly = false,
}: RecurrenceSettingsProps) {
  const { i18n } = useLingui();
  // The field may be empty while the planner types (#2119): "" used to snap back to 1, so
  // clearing the field and typing 5 gave 15. Only a valid number reaches the form.
  const [repetitionsDraft, setRepetitionsDraft] = useState(String(repetitions));
  useEffect(() => {
    setRepetitionsDraft((draft) => (parseInt(draft, 10) === repetitions ? draft : String(repetitions)));
  }, [repetitions]);

  const getRecurrenceIcon = (type: RecurrenceTypeValue) => {
    switch (type) {
      case RecurrenceType.DAILY:
        return <Calendar className="h-4 w-4" />;
      case RecurrenceType.WEEKDAYS:
        return <CalendarDays className="h-4 w-4" />;
      case RecurrenceType.WEEKLY:
        return <CalendarRange className="h-4 w-4" />;
      default:
        return <Repeat className="h-4 w-4" />;
    }
  };

  const getRecurrenceLabel = (type: RecurrenceTypeValue) => {
    switch (type) {
      case RecurrenceType.DAILY:
        return i18n._({
          id: 'shifts.recurrence.daily',
          message: 'Daily',
        });
      case RecurrenceType.WEEKDAYS:
        return i18n._({
          id: 'shifts.recurrence.weekdays',
          message: 'Weekdays (Mon-Fri)',
        });
      case RecurrenceType.WEEKLY:
        return i18n._({
          id: 'shifts.recurrence.weekly',
          message: 'Weekly',
        });
      default:
        return type;
    }
  };

  if (readOnly) {
    return (
      <div
        className="rounded-md border bg-muted/50 p-3"
        data-testid="recurrence-settings-readonly"
      >
        <div className="flex items-center gap-2 mb-2">
          <Repeat className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium text-muted-foreground">
            {i18n._({
              id: 'shifts.series.recurrence',
              message: 'Recurrence',
            })}
          </span>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1.5">
            {getRecurrenceIcon(recurrenceType)}
            <span>{getRecurrenceLabel(recurrenceType)}</span>
          </div>
          <div className="text-muted-foreground">
            {i18n._({
              id: 'shifts.series.repetitions',
              message: '{count} occurrences',
              values: { count: repetitions },
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="space-y-3 rounded-md border p-3"
      data-testid="recurrence-settings-editable"
    >
      <div className="flex items-center gap-2">
        <Repeat className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">
          {i18n._({
            id: 'shifts.series.recurrence',
            message: 'Recurrence',
          })}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="recurrence-type" className="text-xs">
            {i18n._({
              id: 'shifts.series.recurrenceType',
              message: 'Pattern',
            })}
          </Label>
          <Select
            value={recurrenceType}
            onValueChange={(value) =>
              onRecurrenceTypeChange?.(value as RecurrenceTypeValue)
            }
          >
            <SelectTrigger id="recurrence-type" data-testid="recurrence-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={RecurrenceType.DAILY}>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {getRecurrenceLabel(RecurrenceType.DAILY)}
                </div>
              </SelectItem>
              <SelectItem value={RecurrenceType.WEEKDAYS}>
                <div className="flex items-center gap-2">
                  <CalendarDays className="h-4 w-4" />
                  {getRecurrenceLabel(RecurrenceType.WEEKDAYS)}
                </div>
              </SelectItem>
              <SelectItem value={RecurrenceType.WEEKLY}>
                <div className="flex items-center gap-2">
                  <CalendarRange className="h-4 w-4" />
                  {getRecurrenceLabel(RecurrenceType.WEEKLY)}
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="repetitions" className="text-xs">
            {i18n._({
              id: 'shifts.series.repetitionsLabel',
              message: 'Occurrences',
            })}
          </Label>
          <Input
            id="repetitions"
            type="number"
            min={1}
            max={365}
            value={repetitionsDraft}
            onChange={(e) => {
              setRepetitionsDraft(e.target.value);
              const value = parseInt(e.target.value, 10);
              if (value >= 1) onRepetitionsChange?.(value);
            }}
            onBlur={() => setRepetitionsDraft(String(repetitions))}
            data-testid="repetitions-input"
          />
        </div>
      </div>
    </div>
  );
}
